import type {
  HazardousDetailFormModel,
  ProductCatalogEntry,
} from '@fv/client-types'
import type { CfrHazardousMaterial } from '@fv/models'

import { useCfrHazmatSearch } from './useCfrHazmatSearch'

export type CfrHazmatField = 'properShippingName' | 'identificationNumber'

const packingGroupNumbers: Record<
  CfrHazardousMaterial['packingGroups'][number],
  string
> = { I: '1', II: '2', III: '3' }

export const cfrPackingGroupPatch = (
  material: CfrHazardousMaterial,
): { packingGroup?: string } => {
  if (!material.packingGroups?.length) {
    return {
      packingGroup: '',
    }
  }

  if (material.packingGroups.length === 1) {
    return { packingGroup: packingGroupNumbers[material.packingGroups[0]] }
  }

  return {}
}

export const mapCfrMaterialToHazmatFields = (
  material: CfrHazardousMaterial,
): Partial<HazardousDetailFormModel> => ({
  properShippingName: material.properShippingName,
  hazmatId: material.identificationNumber,
  primaryClass: material.primaryClass,
  secondaryClass: material.secondaryClass || '',
  tertiaryClass: material.tertiaryClass || '',
  ...cfrPackingGroupPatch(material),
})

export const mapCfrMaterialToProductFields = (
  material: CfrHazardousMaterial,
): Partial<ProductCatalogEntry> => ({
  properShippingName: material.properShippingName,
  hazmatId: material.identificationNumber,
  hazardClass: material.primaryClass,
  secondaryHazardClass: material.secondaryClass || '',
  tertiaryHazardClass: material.tertiaryClass || '',
  ...cfrPackingGroupPatch(material),
})

export const useRequiresTechnicalName = (properShippingName?: string) => {
  const { data: cfrResults } = useCfrHazmatSearch(
    'properShippingName',
    properShippingName,
  )

  return cfrResults?.[0]?.symbols?.includes('G') ?? false
}

export const useRequirePrimaryClass = (properShippingName?: string) =>
  properShippingName?.toLowerCase() !== 'combustible liquid, n.o.s.'

const normalizeCfrValue = (value: string) => value.trim().toLowerCase()

export const isValidCfrFieldValue = (
  field: CfrHazmatField,
  value: string | undefined,
  materials: CfrHazardousMaterial[] | undefined,
): boolean => {
  const normalized = normalizeCfrValue(value ?? '')
  if (!normalized) return true

  return (materials ?? []).some(
    material => normalizeCfrValue(material[field]) === normalized,
  )
}
